import React, { useEffect, useState } from 'react';
import {
  listQuestions,
  createQuestion,
  updateQuestion,
  archiveQuestion,
  reorderQuestions,
  importQuestionsCsv,
  ApiError,
  type ImportQuestionsResult,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Question, QuestionType } from '../types';
import { DEFAULT_QUESTION_GROUPS } from '../types';

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'number', label: 'Number' },
  { value: 'yes_no', label: 'Yes / No' },
];

const emptyForm = {
  text: '',
  helpText: '',
  type: 'short_text' as QuestionType,
  optionsText: '',
  required: true,
  priorityGroup: '',
  category: '',
  group: 'AWS Migration',
};

export default function QuestionsPage() {
  const { isAdmin } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportQuestionsResult | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  function load() {
    listQuestions()
      .then((res) => setQuestions(res.questions.sort((a, b) => a.order - b.order)))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load questions.'));
  }

  useEffect(load, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const needsOptions = form.type === 'single_choice' || form.type === 'multiple_choice';
    const options = needsOptions
      ? form.optionsText
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean)
      : undefined;

    try {
      const payload = {
        text: form.text,
        helpText: form.helpText || undefined,
        type: form.type,
        options,
        required: form.required,
        priorityGroup: form.priorityGroup,
        category: form.category,
        group: form.group,
      };
      if (editingId) {
        await updateQuestion(editingId, payload);
      } else {
        await createQuestion(payload);
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save question.');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(q: Question) {
    setForm({
      text: q.text,
      helpText: q.helpText ?? '',
      type: q.type,
      optionsText: (q.options ?? []).join('\n'),
      required: q.required,
      priorityGroup: q.priorityGroup,
      category: q.category,
      group: q.group ?? 'AWS Migration',
    });
    setEditingId(q.questionId);
    setShowForm(true);
  }

  async function handleArchive(id: string) {
    if (!confirm('Archive this question? It will no longer appear on new customer links.')) return;
    try {
      await archiveQuestion(id);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive question.');
    }
  }

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    const reordered = [...questions];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setQuestions(reordered);
    try {
      await reorderQuestions(reordered.map((q) => q.questionId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to reorder questions.');
      load();
    }
  }

  const needsOptions = form.type === 'single_choice' || form.type === 'multiple_choice';

  // Suggestions = the built-in defaults plus any group already used by an
  // existing question (including newly created ones) - this is what lets a
  // group an admin types in the form show up everywhere else (filter here,
  // and the customer page's assignment groups) without a fixed enum.
  const knownGroups = Array.from(
    new Set<string>([...DEFAULT_QUESTION_GROUPS, ...questions.map((q) => q.group ?? 'AWS Migration')]),
  ).sort((a, b) => a.localeCompare(b));

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const text = await file.text();
      const result = await importQuestionsCsv(text);
      setImportResult(result);
      if (result.imported > 0) load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to import questions CSV.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>Questions</h1>
        {isAdmin && (
          <div className="row" style={{ flex: 'none', gap: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button
              style={{ flex: 'none' }}
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
            >
              {importing ? 'Importing...' : 'Import from CSV'}
            </button>
            <button
              className="primary"
              style={{ flex: 'none' }}
              onClick={() => {
                setForm(emptyForm);
                setEditingId(null);
                setShowForm((v) => !v);
              }}
            >
              {showForm ? 'Cancel' : 'New question'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {importResult && (
        <div className="card">
          <p>
            Import finished: {importResult.imported} question{importResult.imported === 1 ? '' : 's'} imported
            {importResult.failed > 0 && `, ${importResult.failed} row${importResult.failed === 1 ? '' : 's'} failed`}.
          </p>
          {importResult.errors.length > 0 && (
            <ul>
              {importResult.errors.map((e) => (
                <li key={e.row}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          )}
          <button onClick={() => setImportResult(null)} style={{ minHeight: 32, padding: '4px 8px' }}>
            Dismiss
          </button>
        </div>
      )}

      <div className="field" style={{ maxWidth: 260 }}>
        <label htmlFor="groupFilter">Filter by group</label>
        <select id="groupFilter" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
          <option value="all">All groups</option>
          {knownGroups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>

      {showForm && (
        <form className="card" onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="text">Question text</label>
            <textarea id="text" required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="helpText">Help text</label>
            <input id="helpText" value={form.helpText} onChange={(e) => setForm({ ...form, helpText: e.target.value })} />
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="type">Type</label>
              <select id="type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as QuestionType })}>
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="priorityGroup">Priority</label>
              <input
                id="priorityGroup"
                required
                value={form.priorityGroup}
                onChange={(e) => setForm({ ...form, priorityGroup: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="category">Category</label>
              <input
                id="category"
                required
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="group">Group</label>
              <input
                id="group"
                list="group-suggestions"
                required
                placeholder="e.g. AWS Migration, or type a new group"
                value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
              />
              <datalist id="group-suggestions">
                {knownGroups.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </div>
          </div>
          {needsOptions && (
            <div className="field">
              <label htmlFor="options">Options (one per line)</label>
              <textarea
                id="options"
                value={form.optionsText}
                onChange={(e) => setForm({ ...form, optionsText: e.target.value })}
              />
            </div>
          )}
          <div className="field">
            <label>
              <input
                type="checkbox"
                style={{ width: 'auto', marginRight: 8 }}
                checked={form.required}
                onChange={(e) => setForm({ ...form, required: e.target.checked })}
              />
              Required
            </label>
          </div>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Save changes' : 'Create question'}
          </button>
        </form>
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Question</th>
              <th>Group</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Required</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {questions
              .filter((q) => !q.archived)
              .filter((q) => groupFilter === 'all' || (q.group ?? 'AWS Migration') === groupFilter)
              .map((q) => {
                const index = questions.indexOf(q);
                return (
                <tr key={q.questionId}>
                  <td>
                    {isAdmin && (
                      <>
                        <button onClick={() => move(index, -1)} disabled={index === 0} style={{ minHeight: 32, padding: '4px 8px' }}>
                          ↑
                        </button>{' '}
                        <button
                          onClick={() => move(index, 1)}
                          disabled={index === questions.length - 1}
                          style={{ minHeight: 32, padding: '4px 8px' }}
                        >
                          ↓
                        </button>
                      </>
                    )}
                  </td>
                  <td>{q.text}</td>
                  <td>{q.group ?? 'AWS Migration'}</td>
                  <td>{q.category}</td>
                  <td>{q.priorityGroup}</td>
                  <td>{q.required ? 'Yes' : 'No'}</td>
                  {isAdmin && (
                    <td>
                      <button onClick={() => startEdit(q)} style={{ minHeight: 32, padding: '4px 8px' }}>
                        Edit
                      </button>{' '}
                      <button
                        className="danger"
                        onClick={() => handleArchive(q.questionId)}
                        style={{ minHeight: 32, padding: '4px 8px' }}
                      >
                        Archive
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
